import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | error
  const [data, setData] = useState(null);
  const { clear } = useCart();
  const attempts = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) { clear(); clearedRef.current = true; }
          return;
        }
        if (res.data.status === "expired") { setStatus("expired"); return; }
        attempts.current += 1;
        if (attempts.current >= 8) { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="order-success-page" className="pt-40 pb-32 max-w-3xl mx-auto px-6 text-center min-h-[70vh]">
      {status === "checking" && (
        <>
          <Loader2 size={48} className="mx-auto text-[#C84B31] animate-spin" />
          <h1 className="font-serif-display text-5xl mt-8 tracking-tighter">Finalising your order&hellip;</h1>
          <p className="text-[#7A7A7A] mt-4">Confirming payment with Stripe. This usually takes a few seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={48} className="mx-auto text-[#4A5D23]" />
          <h1 data-testid="success-heading" className="font-serif-display text-6xl mt-8 tracking-tighter">Thank you.</h1>
          <p className="text-lg text-[#4A4A4A] mt-6 leading-relaxed">
            Your order is confirmed. We&apos;ll email tracking &amp; customs paperwork within 24 hours.
            {data?.amount_total && (
              <> · Charged <strong>${(data.amount_total/100).toFixed(2)} {data.currency?.toUpperCase()}</strong>.</>
            )}
          </p>
          <Link data-testid="back-home-link" to="/" className="inline-flex mt-10 bg-[#1A1A1A] text-[#FAF9F6] px-8 py-4 uppercase tracking-overline text-sm hover:bg-[#C84B31]">
            Back to home
          </Link>
        </>
      )}
      {(status === "expired" || status === "error") && (
        <>
          <XCircle size={48} className="mx-auto text-[#C84B31]" />
          <h1 className="font-serif-display text-5xl mt-8 tracking-tighter">We couldn&apos;t confirm your payment.</h1>
          <p className="text-[#4A4A4A] mt-4">If you were charged, please contact us with your session id: <code className="text-xs">{sessionId}</code></p>
          <Link to="/cart" className="inline-flex mt-10 underline text-[#C84B31]">Return to cart</Link>
        </>
      )}
    </div>
  );
}
